import React, { useState, useRef } from "react";
import {
    Container,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    InputAdornment,
    Checkbox, // Import Button component
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { Print } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

const CheckContainer = styled(Container)({
    maxWidth: "600px",
    padding: "20px",
    border: "1px solid black",
    backgroundColor: "white",
    fontFamily: "Courier, monospace",
});

const Header = styled(Typography)({
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "24px",
    marginBottom: "10px",
});

const SummaryText = styled(Typography)({
    fontSize: "16px",
    fontWeight: "thin",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
    gap: "10px",
});

const InvoicePage = ({
    items,
    setItems,
    otherPartyId,
    authHeader,
    invoice,
    typeId,
    isEditMode = false,
    editInvoiceId = null,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const subtotal = items.reduce(
        (acc, item) =>
            acc + (item.quantity * item.price - (item.discountAmount || 0)),
        0
    );

    const totalTax = items.reduce((acc, item) => {
        if (item.isTaxable ?? false) {
            const itemSubtotal =
                item.quantity * item.price - (item.discountAmount || 0);
            const taxOnItem =
                (itemSubtotal * (invoice.taxPercentage || 0)) / 100;
            return acc + taxOnItem;
        }
        return acc;
    }, 0);

    const finalTotal = subtotal + totalTax;

    // Create a ref for the Paper component
    const printRef = useRef(null);
    const reactToPrintFn = useReactToPrint({ contentRef: printRef });

    // Use lineId to identify specific invoice line (so multiple lines with same product id are editable independently)
    const handleItemChange = (lineId, field, value) => {
        if (field === "isTaxable") {
            const updatedItems = items.map((item) => {
                console.log({
                    lineId: item.lineId,
                });
                if (item.lineId === lineId) {
                    console.log("matched lineId");
                    return { ...item, isTaxable: value };
                }
                return item;
            });
            console.log("test:", updatedItems);
            setItems(updatedItems);
            return;
        }
        if (field === "discountAmount") {
            const target = items.find((item) => item.lineId === lineId);
            if (!target) return;
            if (value > target.quantity * target.price) {
                const updatedItems = items.map((item) => {
                    if (item.lineId === lineId) {
                        return {
                            ...item,
                            discountAmount: value,
                            discountPercentage: (
                                (value / (item.quantity * item.price)) *
                                100
                            ).toFixed(4),
                        };
                    }
                    return item;
                });
                setItems(updatedItems);
            } else if (!value) {
                setItems(
                    items.map((item) => {
                        if (item.lineId === lineId) {
                            return {
                                ...item,
                                discountAmount: 0,
                                discountPercentage: 0,
                            };
                        }
                        return item;
                    })
                );
            }
            return;
        }
        if (field === "discountPercentage") {
            if (value > 100) {
                return;
            }
            if (!value) {
                setItems(
                    items.map((item) => {
                        if (item.lineId === lineId) {
                            return {
                                ...item,
                                discountAmount: 0,
                                discountPercentage: 0,
                            };
                        }
                        return item;
                    })
                );
                return;
            }
            const updatedItems = items.map((item) => {
                if (item.lineId === lineId) {
                    return {
                        ...item,
                        discountPercentage: value,
                        discountAmount: (
                            (value / 100) *
                            item.quantity *
                            item.price
                        ).toFixed(4),
                    };
                }
                return item;
            });
            setItems(updatedItems);
            return;
        }

        const updatedItems = items.map((item) => {
            if (item.lineId === lineId) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setItems(updatedItems);
    };

    const handlePrint = () => {
        window.print();
    };

    const navigate = useNavigate();
    const handleSaveInvoice = async () => {
        setIsLoading(true);

        try {
            // Step 1: If in edit mode, delete the original invoice
            if (isEditMode && editInvoiceId) {
                const deleteResponse = await fetch(
                    `${REACT_APP_BACKEND_URL}/invoices/${editInvoiceId}`,
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                    }
                );

                if (!deleteResponse.ok) {
                    throw new Error("Failed to delete original invoice");
                }
            }

            // Step 2: Create the new/updated invoice
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/invoices/create`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },

                    body: JSON.stringify({
                        otherPartyId: otherPartyId,
                        taxPercentage: invoice.taxPercentage,
                        note: invoice.note,
                        dollarToLiraRate: invoice.dollarToLiraRate || null,
                        items: items.map((item) => ({
                            id: item.id,
                            quantity: item.quantity,
                            price: item.price,
                            priceFinal:
                                item.quantity * item.price -
                                (item.discountAmount || 0),
                            discountAmount: item.discountAmount || 0,
                            discountPercentage: item.discountPercentage || 0,
                            isUpdateSpecialPrice: item.isUpdateSpecialPrice,
                            isTaxable: item.isTaxable ?? false,
                        })),
                        typeId,
                        serialId: invoice.serialId || null, // Preserve serialId in edit mode
                    }),
                }
            );

            if (response.ok) {
                const data = await response.json();
                navigate(`/invoice/${data.invoice.id}`);
                setIsSaved(true);
                console.log(data);
            } else {
                const errorResponse = await response.json();
                console.log(errorResponse);
                throw new Error(
                    errorResponse.message || "Failed to save invoice"
                );
            }
        } catch (error) {
            console.error("Error saving invoice:", error);
            alert(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                maxWidth: "900px",
                margin: "0 auto",
                direction: "ltr",
            }}
        >
            <CheckContainer ref={printRef} style={{ overflowY: "scroll" }}>
                {" "}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        align="left"
                        gutterBottom
                        style={{
                            marginBottom: "20px",
                            fontFamily: "Roboto",
                        }}
                    >
                        Dannawi Med Lab
                    </Typography>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "start",
                            flexGrow: "1",
                            direction: "rtl",
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            align="left"
                            style={{
                                fontFamily: "Roboto",
                            }}
                        >
                            ضناوي ميد - لاب
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            align="left"
                            style={{
                                fontSize: "12px",
                                fontFamily: "Roboto",
                            }}
                        >
                            تجارة عامة - إستيراد وتصدير مواد طبية ومخبرية
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            align="left"
                            style={{
                                fontSize: "12px",
                                fontFamily: "Roboto",
                            }}
                        >
                            العنوان: طرابلس - المعرض - ماناساكي ط 4
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            align="left"
                            style={{
                                fontSize: "12px",
                                fontFamily: "Roboto",
                            }}
                        >
                            هاتف: 686724\03 - 910345\70
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            align="left"
                            style={{
                                fontSize: "12px",
                                fontFamily: "Roboto",
                            }}
                        >
                            تلفاكس: 389016\06
                        </Typography>
                    </div>
                </div>
                <TableContainer component={Paper} style={{ boxShadow: "none" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell
                                    align="right"
                                    style={{ textWrap: "nowrap" }}
                                >
                                    Unit Price
                                </TableCell>
                                <TableCell align="right">USP</TableCell>
                                <TableCell align="right">Taxable</TableCell>
                                <TableCell align="right">Discount %</TableCell>
                                <TableCell align="right">Discount $</TableCell>
                                <TableCell align="right">Tax</TableCell>
                                <TableCell align="right">Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.lineId}>
                                    <TableCell>{item.name}</TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            disabled={isSaved}
                                            type="number"
                                            value={item.quantity}
                                            variant="standard"
                                            onChange={(e) =>
                                                handleItemChange(
                                                    item.lineId,
                                                    "quantity",
                                                    parseInt(e.target.value, 10)
                                                )
                                            }
                                            inputProps={{
                                                style: { textAlign: "right" },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right" width={"120px"}>
                                        <TextField
                                            disabled={isSaved}
                                            type="number"
                                            value={item.price}
                                            onChange={(e) =>
                                                handleItemChange(
                                                    item.lineId,
                                                    "price",
                                                    parseFloat(e.target.value)
                                                )
                                            }
                                            variant="standard"
                                            inputProps={{
                                                style: {
                                                    textAlign: "right",
                                                    width: "100px",
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Checkbox
                                            onChange={(e) =>
                                                handleItemChange(
                                                    item.lineId,
                                                    "isUpdateSpecialPrice",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Checkbox
                                            checked={item.isTaxable ?? false}
                                            onChange={(e) =>
                                                handleItemChange(
                                                    item.lineId,
                                                    "isTaxable",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            disabled={isSaved}
                                            type="number"
                                            value={item.discountPercentage}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            %
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        textAlign: "right",
                                                        width: "80px",
                                                    },
                                                },
                                            }}
                                            onChange={(e) =>
                                                handleItemChange(
                                                    item.lineId,
                                                    "discountPercentage",
                                                    parseFloat(e.target.value)
                                                )
                                            }
                                            variant="standard"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            type="number"
                                            disabled={isSaved}
                                            value={item.discountAmount}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            $
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        textAlign: "right",
                                                        width: "80px",
                                                    },
                                                },
                                            }}
                                            onChange={(e) =>
                                                handleItemChange(
                                                    item.lineId,
                                                    "discountAmount",
                                                    parseFloat(e.target.value)
                                                )
                                            }
                                            variant="standard"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        $
                                        {(
                                            ((item.quantity * item.price -
                                                (item.discountAmount || 0)) *
                                                (item.isTaxable ?? false
                                                    ? invoice.taxPercentage || 0
                                                    : 0)) /
                                            100
                                        ).toFixed(4)}
                                    </TableCell>
                                    <TableCell align="right">
                                        $
                                        {(
                                            item.quantity * item.price -
                                            (item.discountAmount || 0) +
                                            ((item.quantity * item.price -
                                                (item.discountAmount || 0)) *
                                                (item.isTaxable ?? false
                                                    ? invoice.taxPercentage || 0
                                                    : 0)) /
                                                100
                                        ).toFixed(4)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <SummaryText>Subtotal: ${subtotal.toFixed(4)}</SummaryText>
                <SummaryText>
                    Tax ({invoice.taxPercentage || 0}%): ${totalTax.toFixed(4)}
                </SummaryText>
                <SummaryText>Final Total: ${finalTotal.toFixed(4)}</SummaryText>
            </CheckContainer>
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => reactToPrintFn()}
                    className="noprint"
                    style={{ marginTop: "20px", alignSelf: "end" }}
                    startIcon={<Print />}
                >
                    Print Invoice
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveInvoice}
                    disabled={isSaved}
                    className="noprint"
                    style={{ marginTop: "20px", alignSelf: "end" }}
                >
                    Save Invoice
                </Button>
            </div>
        </div>
    );
};

export default InvoicePage;
